import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi, isinMasterApi } from '../services/api';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import '../styles/DebenturePage.css';

const DebenturePage = () => {
  const [counterparties, setCounterparties] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [isinList, setIsinList] = useState([]);
  const [cashFlows, setCashFlows] = useState([]);
  const [createdDebentureId, setCreatedDebentureId] = useState(null);
  const [showDebentureDocumentSection, setShowDebentureDocumentSection] = useState(false);
  const [selectedDebentureForDocuments, setSelectedDebentureForDocuments] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Header Details
    investmentId: 'INV-00123456',
    status: 'Pending Approval',
    portfolio: '',
    entity: '',
    fileNumber: '',
    
    // Issuer & Instrument Details
    issuer: '',
    isin: '',
    debentureType: '',
    creditRating: '',
    
    // Pricing & Yield
    quantity: '',
    purchasePrice: '',
    totalInvestment: '',
    currentYield: '',
    yieldToMaturity: '',
    
    // Accounting & GL Mapping
    glAccount: '',
    interestAccrual: '',
    principalGL: '',
    
    // Risk & Limits
    exposureLimit: '',
    exposurePercent: '',
    
    // Tax & Charges
    whtRate: '',
    otherCharges: '',
    
    // Investment Terms
    faceValue: '',
    investmentDate: null,
    maturityDate: null,
    couponRate: '',
    couponFrequency: '',
    tenor: '',
    
    // Cash Flow Mapping
    interestMethod: '',
    principalPayment: ''
  });

  // Fetch initial data
  useEffect(() => {
    fetchCounterparties();
    fetchPortfolios();
    fetchIsins();
    generateInvestmentId();
    calculateCashFlows();
  }, [
    formData.faceValue,
    formData.quantity,
    formData.couponRate,
    formData.couponFrequency,
    formData.investmentDate,
    formData.maturityDate,
    formData.principalPayment
  ]);

  const fetchCounterparties = async () => {
    try {
      const data = await counterpartyApi.getAll();
      setCounterparties(data || []);
    } catch (error) {
      console.error('Error fetching counterparties:', error);
      setCounterparties([]);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/portfolio-master`);
      setPortfolios(response.data || []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
      setPortfolios([]);
    }
  };

  const fetchIsins = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/isin-master`);
      if (response.data && response.data.success) {
        setIsinList(response.data.data || []);
      } else {
        setIsinList([]);
      }
    } catch (error) {
      console.error('Error fetching ISINs:', error);
      setIsinList([]);
    }
  };

  const generateInvestmentId = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 100000).toString().padStart(6, '0');
    setFormData(prev => ({
      ...prev,
      investmentId: `INV-${year}${month}${day}${random}`
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Calculate total investment when quantity or purchase price changes
      if (name === 'quantity' || name === 'purchasePrice') {
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.purchasePrice) || 0;
        updated.totalInvestment = (qty * price).toFixed(2);
      }
      
      // Calculate exposure percentage
      if (name === 'totalInvestment' || name === 'exposureLimit') {
        const total = parseFloat(updated.totalInvestment) || 0;
        const limit = parseFloat(updated.exposureLimit) || 0;
        if (limit > 0) {
          updated.exposurePercent = ((total / limit) * 100).toFixed(2);
        }
      }
      
      return updated;
    });
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const calculateCashFlows = () => {
    if (!formData.investmentDate || !formData.maturityDate || !formData.couponRate || !formData.faceValue || !formData.quantity) {
      setCashFlows([]);
      return;
    }

    const startDate = new Date(formData.investmentDate);
    const endDate = new Date(formData.maturityDate);
    const faceValue = parseFloat(formData.faceValue) || 0;
    const quantity = parseFloat(formData.quantity) || 0;
    const couponRate = parseFloat(formData.couponRate) || 0;
    const principalAmount = faceValue * quantity;
    const annualInterest = (principalAmount * couponRate) / 100;
    
    const frequency = formData.couponFrequency;
    let periodsPerYear = 1;
    if (frequency === 'Semi-Annual') periodsPerYear = 2;
    else if (frequency === 'Quarterly') periodsPerYear = 4;
    else if (frequency === 'Monthly') periodsPerYear = 12;
    
    const interestPerPeriod = annualInterest / periodsPerYear;
    const flows = [];
    
    // Calculate period dates
    let currentDate = new Date(startDate);
    const periodMonths = 12 / periodsPerYear;
    
    while (currentDate < endDate) {
      currentDate = new Date(currentDate);
      currentDate.setMonth(currentDate.getMonth() + periodMonths);
      
      if (currentDate <= endDate) {
        flows.push({
          date: currentDate,
          type: 'Interest',
          interest: interestPerPeriod,
          netCashFlow: interestPerPeriod
        });
      }
    }
    
    // Add final payment with principal
    if (formData.principalPayment === 'Bullet') {
      flows.push({
        date: endDate,
        type: 'Interest + Principal',
        interest: interestPerPeriod,
        netCashFlow: interestPerPeriod + principalAmount
      });
    } else {
      // For other payment methods, add principal separately
      flows.push({
        date: endDate,
        type: 'Interest',
        interest: interestPerPeriod,
        netCashFlow: interestPerPeriod
      });
      flows.push({
        date: endDate,
        type: 'Principal',
        interest: 0,
        netCashFlow: principalAmount
      });
    }
    
    setCashFlows(flows);
  };

  useEffect(() => {
    calculateCashFlows();
  }, [
    formData.investmentDate,
    formData.maturityDate,
    formData.couponRate,
    formData.couponFrequency,
    formData.faceValue,
    formData.quantity,
    formData.principalPayment
  ]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const totalCashFlows = cashFlows.reduce((sum, flow) => sum + (parseFloat(flow.netCashFlow) || 0), 0);

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        investmentDate: formData.investmentDate ? formData.investmentDate.toISOString().split('T')[0] : null,
        maturityDate: formData.maturityDate ? formData.maturityDate.toISOString().split('T')[0] : null,
        cashFlows: cashFlows.map(cf => ({
          ...cf,
          date: cf.date.toISOString().split('T')[0]
        }))
      };
      
      const response = await axios.post(`${API_BASE_URL}/debenture/investments`, payload);
      const investmentId = response.data?.data?.id || response.data?.id || payload.investmentId;
      setCreatedDebentureId(investmentId);
      setShowDebentureDocumentSection(true);
      alert('Saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save');
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      const payload = {
        ...formData,
        status: 'Pending Approval',
        investmentDate: formData.investmentDate ? formData.investmentDate.toISOString().split('T')[0] : null,
        maturityDate: formData.maturityDate ? formData.maturityDate.toISOString().split('T')[0] : null,
        cashFlows: cashFlows.map(cf => ({
          ...cf,
          date: cf.date.toISOString().split('T')[0]
        }))
      };
      
      const response = await axios.post(`${API_BASE_URL}/debenture/investments`, payload);
      const investmentId = response.data?.data?.id || response.data?.id || payload.investmentId;
      setCreatedDebentureId(investmentId);
      setShowDebentureDocumentSection(true);
      alert('Submitted for approval successfully');
      generateInvestmentId();
    } catch (error) {
      console.error('Error submitting for approval:', error);
      alert('Failed to submit for approval');
    }
  };

  return (
    <>
    <div className="debenture-page">
      {/* Header Section */}
      <div className="deb-header">
        <div className="deb-header-info">
          <div className="deb-header-item">
            <label>Investment ID:</label>
            <span>{formData.investmentId}</span>
          </div>
          <div className="deb-header-item">
            <label>Status:</label>
            <span className="status-badge status-pending">{formData.status}</span>
          </div>
          <div className="deb-header-item">
            <label>Portfolio:</label>
            <select 
              name="portfolio" 
              value={formData.portfolio} 
              onChange={handleInputChange}
            >
              <option value="">Select Portfolio</option>
              {portfolios.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="deb-header-item">
            <label>Entity:</label>
            <input 
              type="text" 
              name="entity" 
              value={formData.entity} 
              onChange={handleInputChange}
              placeholder="ABC Holdings Ltd."
            />
          </div>
          <div className="deb-header-item">
            <label>File Number:</label>
            <input 
              type="text" 
              name="fileNumber" 
              value={formData.fileNumber} 
              onChange={handleInputChange}
              placeholder="Enter file number"
            />
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="deb-main-sections">
        {/* Left Column */}
        <div className="deb-left-column">
          {/* Issuer & Instrument Details */}
          <div className="deb-section-card">
            <h3>Issuer & Instrument Details</h3>
            
            <div className="deb-form-group">
              <label>Issuer</label>
              <input 
                type="text" 
                name="issuer" 
                value={formData.issuer} 
                onChange={handleInputChange}
                placeholder="XYZ Corporation"
              />
            </div>

            <div className="deb-form-group">
              <label>ISIN</label>
              <select 
                name="isin" 
                value={formData.isin} 
                onChange={handleInputChange}
              >
                <option value="">Select ISIN</option>
                {isinList.map(isin => (
                  <option key={isin.isin_number} value={isin.isin_number}>
                    {isin.isin_number} - {isin.isin_issuer || 'Security'}
                  </option>
                ))}
              </select>
            </div>

            <div className="deb-form-group">
              <label>Debenture Type</label>
              <select 
                name="debentureType" 
                value={formData.debentureType} 
                onChange={handleInputChange}
              >
                <option value="">Select Type</option>
                <option value="Secured Redeemable">Secured Redeemable</option>
                <option value="Unsecured Redeemable">Unsecured Redeemable</option>
                <option value="Secured Irredeemable">Secured Irredeemable</option>
                <option value="Unsecured Irredeemable">Unsecured Irredeemable</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Credit Rating</label>
              <select 
                name="creditRating" 
                value={formData.creditRating} 
                onChange={handleInputChange}
              >
                <option value="">Select Rating</option>
                <option value="AAA">AAA</option>
                <option value="AA+(Icra Lanka)">AA+(Icra Lanka)</option>
                <option value="AA-(Icra Lanka)">AA-(Icra Lanka)</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="BBB+">BBB+</option>
                <option value="BBB-">BBB-</option>
              </select>
            </div>
          </div>

          {/* Pricing & Yield */}
          <div className="deb-section-card">
            <h3>Pricing & Yield</h3>
            
            <div className="deb-form-group">
              <label>Quantity</label>
              <input 
                type="number" 
                name="quantity" 
                value={formData.quantity} 
                onChange={handleInputChange}
                placeholder="10,000"
                step="1"
              />
            </div>

            <div className="deb-form-group">
              <label>Purchase Price</label>
              <input 
                type="number" 
                name="purchasePrice" 
                value={formData.purchasePrice} 
                onChange={handleInputChange}
                placeholder="98.50"
                step="0.01"
              />
            </div>

            <div className="deb-form-group">
              <label>Total Investment</label>
              <input 
                type="text" 
                name="totalInvestment" 
                value={formatCurrency(formData.totalInvestment || 0)} 
                readOnly
                className="readonly"
              />
            </div>

            <div className="deb-form-group">
              <label>Current Yield</label>
              <select 
                name="currentYield" 
                value={formData.currentYield} 
                onChange={handleInputChange}
              >
                <option value="">Select Yield</option>
                <option value="10.82%">10.82%</option>
                <option value="11.00%">11.00%</option>
                <option value="11.25%">11.25%</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Yield to Maturity (YTM)</label>
              <select 
                name="yieldToMaturity" 
                value={formData.yieldToMaturity} 
                onChange={handleInputChange}
              >
                <option value="">Select YTM</option>
                <option value="11.25%">11.25%</option>
                <option value="11.50%">11.50%</option>
                <option value="12.00%">12.00%</option>
              </select>
            </div>
          </div>

          {/* Accounting & GL Mapping */}
          <div className="deb-section-card">
            <h3>Accounting & GL Mapping</h3>
            
            <div className="deb-form-group">
              <label>GL Account</label>
              <select 
                name="glAccount" 
                value={formData.glAccount} 
                onChange={handleInputChange}
              >
                <option value="">Select GL Account</option>
                <option value="Debenture Investments">Debenture Investments</option>
                <option value="Bond Investments">Bond Investments</option>
                <option value="Fixed Income Investments">Fixed Income Investments</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Interest Accrual</label>
              <select 
                name="interestAccrual" 
                value={formData.interestAccrual} 
                onChange={handleInputChange}
              >
                <option value="">Select Account</option>
                <option value="DR-Interest Income">DR-Interest Income</option>
                <option value="CR-Interest Income">CR-Interest Income</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Principal GL</label>
              <select 
                name="principalGL" 
                value={formData.principalGL} 
                onChange={handleInputChange}
              >
                <option value="">Select Account</option>
                <option value="CR-Debenture Principal">CR-Debenture Principal</option>
                <option value="DR-Debenture Principal">DR-Debenture Principal</option>
              </select>
            </div>
          </div>

          {/* Risk & Limits */}
          <div className="deb-section-card">
            <h3>Risk & Limits</h3>
            
            <div className="deb-form-group">
              <label>Exposure Limit</label>
              <input 
                type="number" 
                name="exposureLimit" 
                value={formData.exposureLimit} 
                onChange={handleInputChange}
                placeholder="20,000,000.00"
                step="0.01"
              />
            </div>

            <div className="deb-form-group">
              <label>Exposure %</label>
              <input 
                type="text" 
                name="exposurePercent" 
                value={formData.exposurePercent ? `${formData.exposurePercent}%` : ''} 
                readOnly
                className="readonly"
              />
            </div>
          </div>

          {/* Tax & Charges */}
          <div className="deb-section-card">
            <h3>Tax & Charges</h3>
            
            <div className="deb-form-group">
              <label>WHT Rate</label>
              <select 
                name="whtRate" 
                value={formData.whtRate} 
                onChange={handleInputChange}
              >
                <option value="">Select Rate</option>
                <option value="5.00%">5.00%</option>
                <option value="10.00%">10.00%</option>
                <option value="15.00%">15.00%</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Other Charges</label>
              <input 
                type="number" 
                name="otherCharges" 
                value={formData.otherCharges} 
                onChange={handleInputChange}
                placeholder="15,000.00"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="deb-right-column">
          {/* Investment Terms */}
          <div className="deb-section-card">
            <h3>Investment Terms</h3>
            
            <div className="deb-form-group">
              <label>Face Value</label>
              <input 
                type="number" 
                name="faceValue" 
                value={formData.faceValue} 
                onChange={handleInputChange}
                placeholder="1,000.00"
                step="0.01"
              />
            </div>

            <div className="deb-form-group">
              <label>Investment Date</label>
              <DatePicker
                selected={formData.investmentDate}
                onChange={(date) => handleDateChange('investmentDate', date)}
                dateFormat="dd/MM/yyyy"
                className="deb-date-input"
                placeholderText="15/02/2024"
              />
            </div>

            <div className="deb-form-group">
              <label>Maturity Date</label>
              <DatePicker
                selected={formData.maturityDate}
                onChange={(date) => handleDateChange('maturityDate', date)}
                dateFormat="dd/MM/yyyy"
                className="deb-date-input"
                placeholderText="15/02/2029"
              />
            </div>

            <div className="deb-form-group">
              <label>Coupon Rate</label>
              <select 
                name="couponRate" 
                value={formData.couponRate} 
                onChange={handleInputChange}
              >
                <option value="">Select Rate</option>
                <option value="10.50">10.50%</option>
                <option value="11.00">11.00%</option>
                <option value="11.50">11.50%</option>
                <option value="12.00">12.00%</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Coupon Frequency</label>
              <select 
                name="couponFrequency" 
                value={formData.couponFrequency} 
                onChange={handleInputChange}
              >
                <option value="">Select Frequency</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Tenor</label>
              <select 
                name="tenor" 
                value={formData.tenor} 
                onChange={handleInputChange}
              >
                <option value="">Select Tenor</option>
                <option value="1 Year">1 Year</option>
                <option value="2 Years">2 Years</option>
                <option value="3 Years">3 Years</option>
                <option value="5 Years">5 Years</option>
                <option value="7 Years">7 Years</option>
                <option value="10 Years">10 Years</option>
              </select>
            </div>
          </div>

          {/* Cash Flow Mapping */}
          <div className="deb-section-card">
            <h3>Cash Flow Mapping</h3>
            
            <div className="deb-form-group">
              <label>Interest Method</label>
              <select 
                name="interestMethod" 
                value={formData.interestMethod} 
                onChange={handleInputChange}
              >
                <option value="">Select Method</option>
                <option value="Actual/Actual">Actual/Actual</option>
                <option value="30/360">30/360</option>
                <option value="Actual/365">Actual/365</option>
              </select>
            </div>

            <div className="deb-form-group">
              <label>Principal Payment</label>
              <select 
                name="principalPayment" 
                value={formData.principalPayment} 
                onChange={handleInputChange}
              >
                <option value="">Select Payment</option>
                <option value="Bullet">Bullet</option>
                <option value="Amortizing">Amortizing</option>
                <option value="Balloon">Balloon</option>
              </select>
            </div>
          </div>

          {/* Cash Flow Preview */}
          <div className="deb-section-card">
            <h3>Cash Flow Preview</h3>
            <div className="deb-table-container">
              <table className="deb-cashflow-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Cash Flow Type</th>
                    <th>Interest</th>
                    <th>Net Cash Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {cashFlows.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        Enter investment details to calculate cash flows
                      </td>
                    </tr>
                  ) : (
                    cashFlows.map((flow, index) => (
                      <tr key={index}>
                        <td>{formatDate(flow.date)}</td>
                        <td>{flow.type}</td>
                        <td>{formatCurrency(flow.interest)}</td>
                        <td>{formatCurrency(flow.netCashFlow)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Documents & Notes */}
          <div className="deb-section-card">
            <h3>Documents & Notes</h3>
            
            <button className="deb-upload-btn">
              <span className="icon">☁</span>
              Upload Document
            </button>

            <button className="deb-add-note-btn">
              <span className="icon">+</span>
              Add Note
            </button>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="deb-footer">
        <div className="deb-footer-left">
          <div className="deb-total-cashflows">
            <label>Total Cash Flows:</label>
            <span>{formatCurrency(totalCashFlows)}</span>
          </div>
        </div>
        <div className="deb-footer-right">
          <button className="deb-btn deb-btn-secondary" onClick={handleSave}>
            Save
          </button>
          <button className="deb-btn deb-btn-primary" onClick={handleSubmitForApproval}>
            Submit for Approval
          </button>
          <button className="deb-btn deb-btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
    
    {/* Document Upload Section - Show after investment is created or when viewing existing investment */}
    {(showDebentureDocumentSection && createdDebentureId) || selectedDebentureForDocuments ? (
      <div style={{ marginTop: '24px', width: '100%', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>
            Documents
            {(createdDebentureId || selectedDebentureForDocuments?.id || selectedDebentureForDocuments?.investmentId) && (
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                - Investment ID: {createdDebentureId || selectedDebentureForDocuments?.id || selectedDebentureForDocuments?.investmentId}
              </span>
            )}
          </h4>
          {selectedDebentureForDocuments && (
            <button 
              className="btn btn-sm btn-secondary" 
              onClick={() => setSelectedDebentureForDocuments(null)}
            >
              Close
            </button>
          )}
        </div>
        <DocumentUpload
          transactionType="debenture"
          transactionId={createdDebentureId || selectedDebentureForDocuments?.id || selectedDebentureForDocuments?.investmentId}
          onUploadSuccess={(documents) => {
            setShowDebentureDocumentSection(false);
          }}
          onUploadError={(error) => {
            console.error('Failed to upload document:', error);
          }}
          multiple={true}
        />
        <DocumentList
          transactionType="debenture"
          transactionId={createdDebentureId || selectedDebentureForDocuments?.id || selectedDebentureForDocuments?.investmentId}
          onDocumentDeleted={() => {}}
        />
      </div>
    ) : null}
    </>
  );
};

export default DebenturePage;
